import React, { useState, useCallback, useMemo } from "react";
import { Modal, Form, ButtonGroup } from "react-bootstrap";
import { useSetError } from "../../redux/modals";
import { awaitJSON, SlidesLambdaFetch } from "../../utils/fetch-utils";
import useLocation from "../../utils/hooks/useLocation";
import { useSelector } from "react-redux";
import * as UserRedux from "../../redux/user";
import roleMap from "../../UserAdmin";
import { Button, ButtonType } from "../Button";
import "./BugTicketModal.scss";
import { StateSetter } from "../../utils/types";
import { ErrorParams } from "@blisspointmedia/bpm-types/src/Page";

interface BugTicketModalProps {
  setShowBugTicketModal: StateSetter<boolean>;
  bugErrorParams?: ErrorParams | undefined;
}
export const BugTicketModal: React.FC<BugTicketModalProps> = ({
  bugErrorParams,
  setShowBugTicketModal,
}) => {
  const setError = useSetError();

  const { company, location } = useLocation();
  const role = useSelector(UserRedux.roleSelector);
  const textRole = roleMap[role];
  const [summary, setSummary] = useState("");
  const [description, setDescription] = useState("");
  const [loading, setLoading] = useState(false);
  const [doc, setDoc] = useState("");
  const [steps, setSteps] = useState("");
  const [blocker, setBlocker] = useState("");
  const [dueDate, setDueDate] = useState("");

  const paramsToString = (params: Record<string, string>) => {
    return Object.keys(params)
      .map(key => `${key}: ${params[key]}`)
      .join(", ");
  };
  const engDetails = useMemo(() => {
    let { pathname } = location;
    let editedParams = "";
    if (bugErrorParams?.params) {
      editedParams =
        typeof bugErrorParams?.params !== "string" ? paramsToString(bugErrorParams?.params) : "N/A";
    }
    return `Bug path: https://blisspoint.tinuiti.com${pathname}\n\nUser role: ${textRole}\n\nCID: ${company}\n\nError Message: ${
      bugErrorParams?.message || ""
    }\n\nRequest Params: ${editedParams}\n\nLambda Path: ${bugErrorParams?.path || ""}`;
  }, [
    bugErrorParams?.message,
    bugErrorParams?.params,
    bugErrorParams?.path,
    company,
    location,
    textRole,
  ]);

  const submit = useCallback(async () => {
    if (!summary || !description || !steps || !blocker || !dueDate) {
      setError({
        variant: "danger",
        title: "Missing Required Fields",
        message: "Please fill out all fields before submitting",
      });
      return;
    }
    setLoading(true); //TODO add actual attachment to send to the ticket
    try {
      let res = await SlidesLambdaFetch("/feedback", {
        method: "POST",
        body: {
          company: company,
          summary,
          description: `${description}\n\n${engDetails}\n\nSteps to recreate:\n\n${steps}\n\nIs this is a blocker?\n\n${blocker}\n\nDue in:\n\n${dueDate}\n\n Attachment: ${doc}`,
        },
      });
      let { link } = await awaitJSON(res);
      setError({
        variant: "success",
        title: "Ticket Created",
        message: (
          <a target="_blank" rel="noopener noreferrer" href={link}>
            {link}
          </a>
        ),
      });
      setLoading(false);
      setShowBugTicketModal(false);
    } catch (e) {
      setError({
        message: e instanceof Error ? e.message : String(e),
        reportError: e,
      });
      setLoading(false);
    }
  }, [
    summary,
    setShowBugTicketModal,
    description,
    setError,
    company,
    steps,
    doc,
    blocker,
    dueDate,
    engDetails,
  ]);

  return (
    <>
      {
        <Modal className="bugModal" show onHide={() => setShowBugTicketModal(false)}>
          <Modal.Header className="bugModalHeader">
            <Modal.Title className="bugModalTitle">Report an Issue</Modal.Title>
          </Modal.Header>
          <Modal.Body className="bugModalBody">
            <Form.Group className="bugModalInputSection">
              <Form.Label className="bugInputTitles">Ticket Title</Form.Label>
              <Form.Control
                className="bugModalInputFields"
                required
                type="text"
                placeholder="Provide a short, clear title for the issue."
                disabled={loading}
                value={summary}
                onChange={e => setSummary(e.currentTarget.value)}
              />
            </Form.Group>
            <Form.Group className="bugModalInputSection">
              <Form.Label className="bugInputTitles">Describe the Issue</Form.Label>
              <Form.Control
                className="bugModalInputFields"
                required
                type="text"
                placeholder="Briefly explain what's happening."
                disabled={loading}
                as="textarea"
                rows={1}
                value={description}
                onChange={e => setDescription(e.currentTarget.value)}
              />
            </Form.Group>
            <Form.Group className="bugModalInputSection">
              <Form.Label className="bugInputTitles"> Steps to Reproduce</Form.Label>
              <Form.Control
                className="bugModalInputFields"
                required
                type="text"
                placeholder="List the actions you took before encountering the issue."
                disabled={loading}
                as="textarea"
                rows={1}
                value={steps}
                onChange={e => setSteps(e.currentTarget.value)}
              />
            </Form.Group>
            <Form.Group className="bugModalInputSection">
              <Form.Label className="bugInputTitles">Is this blocking your work?</Form.Label>
              <Form.Control
                className="bugModalInputFields"
                required
                type="text"
                placeholder="If yes, describe what you're unable to complete. If not, write No."
                disabled={loading}
                as="textarea"
                rows={1}
                value={blocker}
                onChange={e => setBlocker(e.currentTarget.value)}
              />
            </Form.Group>
            <Form.Group className="bugModalInputSection">
              <Form.Label className="bugInputTitles">When do you need a fix?</Form.Label>
              <Form.Control
                className="bugModalInputFields"
                required
                type="text"
                placeholder="Specify the latest date this needs to be resolved."
                disabled={loading}
                as="textarea"
                rows={1}
                value={dueDate}
                onChange={e => setDueDate(e.currentTarget.value)}
              />
            </Form.Group>
            <Form.Group className="bugModalInputSection">
              <Form.Label className="bugInputTitles">Attach a file (optional)</Form.Label>
              <Form.Control
                className="bugModalInputFields"
                required
                type="text"
                placeholder="Upload any relevant screenshots or documents."
                disabled={loading}
                as="textarea"
                rows={1}
                value={doc}
                onChange={e => setDoc(e.currentTarget.value)}
              />
            </Form.Group>
          </Modal.Body>
          <ButtonGroup className="submitButtonGroup">
            <Button
              type={ButtonType.EMPTY}
              disabled={loading}
              onClick={() => setShowBugTicketModal(false)}
            >
              Cancel
            </Button>
            <Button type={ButtonType.FILLED} disabled={loading} onClick={submit}>
              Submit
            </Button>
          </ButtonGroup>
        </Modal>
      }
    </>
  );
};

export default BugTicketModal;
